import type { OptionsMap, OptionsType } from '@openx/types/options';
import {
  type ContentObjectOption,
  ContentObjectOptions,
  type ContentOptionsGroup,
  type OptionsConfig,
} from '@openx/types/targeting/contentObject';
import type { OptionItem } from '@openx/types/targeting/targeting';
import { prepareMapBy } from '@openx/utils/lib/prepareMapBy';
import { produceSortedOptions } from '@openx/utils/lib/sortedOptions';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useOptionsByPathQuery } from '../../../hooks/useOptionsByPathQuery';

type UseOptionsFetchParams = {
  optionType: OptionsType;
  errorMsg: string;
  mapByKey?: string;
};
const useOptionsFetch = ({ optionType, mapByKey = 'id' }: UseOptionsFetchParams) => {
  const path = `deal.package.targeting.instream_content.${optionType}`;

  const {
    data: { optionsByPath = [] } = {},
    loading,
    refetch,
  } = useOptionsByPathQuery({
    variables: {
      path,
    },
  });

  const list = useMemo(() => {
    return optionsByPath.map(option =>
      option
        ? {
            id: option.id,
            name: option.name,
            ...option.extra,
          }
        : []
    );
  }, [optionsByPath]);

  const data = produceSortedOptions(prepareMapBy(list, mapByKey));

  return {
    data: (data || {}) as OptionsMap,
    isLoading: loading,
    refetch,
  };
};

const useGetOption = (type: ContentObjectOption): OptionItem => {
  const { t } = useTranslation();
  const isTypeLanguage = type === ContentObjectOptions.LANGUAGE;

  const { isLoading, data } = useOptionsFetch({
    errorMsg: t('Failed to fetch {type} options', { type }),
    mapByKey: !isTypeLanguage ? 'name' : 'id',

    // TODO UIG-540 remove casting when all options are implemented
    optionType: type as OptionsType,
  });

  return { loading: isLoading, options: data };
};

export const useContentObjectOptionsConfig = (): OptionsConfig & {
  loading?: boolean;
  options?: OptionsMap;
} => {
  const { t } = useTranslation();

  const optionGroup: ContentOptionsGroup = {
    [ContentObjectOptions.CHANNEL]: false,
    [ContentObjectOptions.CONTENT_RATING]: true,
    [ContentObjectOptions.EPISODE]: false,
    [ContentObjectOptions.GENRE]: true,
    [ContentObjectOptions.LANGUAGE]: true,
    length: undefined,
    [ContentObjectOptions.LIVESTREAM]: {
      loading: false,
      options: {
        0: { id: '0', name: t('False') },
        1: { id: '1', name: t('True') },
      },
    },
    [ContentObjectOptions.NETWORK]: false,
    [ContentObjectOptions.SERIES]: false,
  };

  const optionsConfig = {
    ...optionGroup,
    useOptionFetch: useGetOption,
  };

  return optionsConfig;
};
